import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { apiService } from "../../api/services/apiService";
import type { EntityPmgAssignment, EntityType } from "../../types";
import { EntityType as EntityTypeEnum } from "../../types";

const ASSIGNABLE_TYPES: EntityType[] = [
  EntityTypeEnum.PRODUCT,
  EntityTypeEnum.API,
  EntityTypeEnum.CATEGORY,
  EntityTypeEnum.DOCUMENT,
  EntityTypeEnum.LINK,
  EntityTypeEnum.FAQ,
];

interface AssetOption {
  uuid: string;
  name: string;
  entityType: EntityType;
}

interface PmgAssetAssignmentPickerProps {
  pmgUuid: string;
  onChanged?: () => void;
}

const PmgAssetAssignmentPicker: React.FC<PmgAssetAssignmentPickerProps> = ({
  pmgUuid,
  onChanged,
}) => {
  const [assignments, setAssignments] = useState<EntityPmgAssignment[]>([]);
  const [entityType, setEntityType] = useState<EntityType>(EntityTypeEnum.PRODUCT);
  const [assetOptions, setAssetOptions] = useState<AssetOption[]>([]);
  const [selectedAssets, setSelectedAssets] = useState<AssetOption[]>([]);
  const [loadingAssignments, setLoadingAssignments] = useState(true);
  const [loadingAssets, setLoadingAssets] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadAssignments = useCallback(async () => {
    setLoadingAssignments(true);
    try {
      const list = await apiService.getPmgAssignedAssets(pmgUuid);
      setAssignments(list);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to load assigned assets");
    } finally {
      setLoadingAssignments(false);
    }
  }, [pmgUuid]);

  useEffect(() => {
    void loadAssignments();
  }, [loadAssignments]);

  useEffect(() => {
    const loadAssets = async () => {
      setLoadingAssets(true);
      try {
        let options: AssetOption[] = [];
        switch (entityType) {
          case EntityTypeEnum.PRODUCT:
            options = (await apiService.getProducts()).map((p) => ({
              uuid: p.uuid,
              name: p.name,
              entityType,
            }));
            break;
          case EntityTypeEnum.API:
            options = (await apiService.getApis()).map((a) => ({
              uuid: a.uuid,
              name: a.name,
              entityType,
            }));
            break;
          case EntityTypeEnum.CATEGORY:
            options = (await apiService.getCategories()).map((c) => ({
              uuid: c.uuid,
              name: c.name,
              entityType,
            }));
            break;
          case EntityTypeEnum.DOCUMENT:
            options = (await apiService.getDocuments()).map((d) => ({
              uuid: d.uuid,
              name: d.name,
              entityType,
            }));
            break;
          case EntityTypeEnum.FAQ:
            options = (await apiService.getFaqs()).map((f) => ({
              uuid: f.uuid,
              name: f.name,
              entityType,
            }));
            break;
          case EntityTypeEnum.LINK:
            options = (await apiService.getLinks()).map((l) => ({
              uuid: l.uuid,
              name: l.name,
              entityType,
            }));
            break;
          default:
            options = [];
        }
        const assignedIds = new Set(assignments.map((a) => a.entityUuid));
        setAssetOptions(options.filter((o) => !assignedIds.has(o.uuid)));
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : "Failed to load assets");
      } finally {
        setLoadingAssets(false);
      }
    };
    void loadAssets();
  }, [entityType, assignments]);

  const handleBulkAssign = async () => {
    if (selectedAssets.length === 0) return;
    setSaving(true);
    setError(null);
    try {
      await apiService.bulkAssignPmgToEntities(
        pmgUuid,
        selectedAssets.map((asset) => ({
          entityUuid: asset.uuid,
          entityType: asset.entityType,
        })),
      );
      setSelectedAssets([]);
      await loadAssignments();
      onChanged?.();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to assign assets");
    } finally {
      setSaving(false);
    }
  };

  const handleUnassign = async (entityUuid: string) => {
    setSaving(true);
    setError(null);
    try {
      await apiService.unassignPmgFromEntity(pmgUuid, entityUuid);
      await loadAssignments();
      onChanged?.();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to unassign asset");
    } finally {
      setSaving(false);
    }
  };

  const assignmentChips = useMemo(
    () =>
      assignments.map((a) => (
        <Chip
          key={`${a.entityUuid}-${a.entityType ?? ""}`}
          label={`${a.entityType ?? "ASSET"}: ${a.entityName ?? a.entityUuid}`}
          size="small"
          onDelete={() => void handleUnassign(a.entityUuid)}
          disabled={saving}
        />
      )),
    [assignments, saving],
  );

  if (loadingAssignments) {
    return <CircularProgress size={20} />;
  }

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle2" gutterBottom>
        Assigned assets
      </Typography>
      {error && (
        <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 2, minHeight: 28 }}>
        {assignments.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No assets assigned yet.
          </Typography>
        ) : (
          assignmentChips
        )}
      </Box>

      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "flex-start" }}>
        <TextField
          select
          size="small"
          label="Asset type"
          value={entityType}
          onChange={(e) => setEntityType(e.target.value as EntityType)}
          sx={{ minWidth: 140 }}
        >
          {ASSIGNABLE_TYPES.map((type) => (
            <MenuItem key={type} value={type}>
              {type}
            </MenuItem>
          ))}
        </TextField>
        <Autocomplete
          multiple
          options={assetOptions}
          getOptionLabel={(o) => o.name}
          value={selectedAssets}
          onChange={(_e, value) => setSelectedAssets(value)}
          loading={loadingAssets}
          renderInput={(params) => (
            <TextField {...params} size="small" label="Select assets" placeholder="Search" />
          )}
          sx={{ minWidth: 280, flex: 1 }}
        />
        <Button
          variant="outlined"
          size="small"
          onClick={() => void handleBulkAssign()}
          disabled={saving || selectedAssets.length === 0}
        >
          Assign selected
        </Button>
      </Box>
    </Box>
  );
};

export default PmgAssetAssignmentPicker;
